import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import router, { useRouter } from 'next/router';
import { message } from 'antd';
import { Auth, Hub } from 'aws-amplify';
import { useOnboardingStateContext } from 'context/onboarding/OnboardingStateContext';
import type { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from 'react-query';

import { HubCapsule } from '@aws-amplify/core';
import { ApiProxy } from '@lib/api/apiProxy';
import { UsersQueryApi } from '@lib/api/endpoints';
import { UsersCommandApi } from '@lib/api/endpoints/users';
import { isPostHogLoaded, posthog } from '@lib/posthog.lib';
import {
  resetStore as resetStoreAction,
  setUserAppearance,
  setUserProfile,
} from '@lib/redux.lib/slices/appSlice';
import type { AppDispatch } from '@lib/redux.lib/store';
import { useAppDispatch } from '@lib/redux.lib/store';
import { AuthUserSession } from '@lib/types/authUser.types';
import { EverysetUser } from '@lib/types/user.types';
import {
  getIsPublicApplicationViewFromRouterAsPath,
  removeTrailingSlashes,
} from '@lib/utils/urlHelper';

import { googleSignout } from './googleSignout';
import { AuthListener } from './listener';

const setupSession = (
  setAuthUserSession: (session: AuthUserSession) => void,
  setOnboardingLoading: (value: boolean) => void,
  setSessionLoading: (value: boolean) => void,
  canStopSessionLoadingRef: MutableRefObject<boolean>,
  setUserProfileData: (userData: EverysetUser) => void,
  setUserAppearanceData: (userAppearance: UserAppearance) => void,
  customState?: string
) => {
  const {
    redirectTo,
    signupProperties: rawSignupProperties,
    birthday,
  } = JSON.parse(customState ?? '{}');

  if (rawSignupProperties?.selectedCountry) {
    canStopSessionLoadingRef.current = false;
  }

  return Auth.currentAuthenticatedUser()
    .then(async (sessionData) => {
      const userId =
        sessionData.attributes?.['custom:castifi_user_id'] ||
        sessionData.signInUserSession.idToken.payload['custom:castifi_user_id'];
      const finalUserId = parseInt(userId);

      const [userData, userAppearance] = await Promise.all([
        UsersQueryApi.getUser(finalUserId),
        UsersQueryApi.getUserAppearance(finalUserId),
      ]);

      setUserProfileData(userData);
      setUserAppearanceData(userAppearance);

      setOnboardingLoading(true);

      if (birthday) {
        await UsersCommandApi.createUserAppearance(finalUserId, { birthday });
        setUserAppearanceData({ ...userAppearance, birthday });
      }

      if (rawSignupProperties?.selectedCountry) {
        const signupProperties = {
          selected_country: rawSignupProperties.selectedCountry,
          referrer: window?.location.origin,
          client_id: process.env.NEXT_PUBLIC_CLIENT_ID,
        };
        const { selected_country } =
          await UsersCommandApi.create_user_signup_properties(
            userId,
            signupProperties
          );

        // after setting the country is done, we need to set the user profile with the country
        setUserProfileData({ ...userData, selected_country });
        canStopSessionLoadingRef.current = true;
      }

      if (redirectTo) {
        await router.push(redirectTo);
        setOnboardingLoading(false);
        setAuthUserSession(sessionData);
      }

      if (userData.bg_onboarded === false) {
        const { asPath } = router ?? {};

        setOnboardingLoading(false);

        if (['/privacy-policy', '/terms-of-service'].includes(asPath)) {
          await router?.push({
            pathname: asPath,
          });

          return;
        }

        await router?.push({
          pathname: '/onboarding',
          ...(asPath.includes('/apps/')
            ? { query: { redirectTo: asPath } }
            : null),
        });
      }

      setAuthUserSession(sessionData);
    })
    .catch(() => {
      setOnboardingLoading(false);
      canStopSessionLoadingRef.current = true;
    })
    .finally(() => {
      if (canStopSessionLoadingRef.current !== false) {
        setSessionLoading(false);
      }
    });
};

const takedownSession = async (
  setAuthUserSession: Dispatch<SetStateAction<AuthUserSession>>,
  appDispatch: AppDispatch
) => {
  googleSignout();
  await (Auth as any)._storage.clear();
  await (Auth as any).cleanCachedItems();
  ApiProxy.resetToken();
  appDispatch(resetStoreAction());

  return setAuthUserSession(null);
};

type UseSessionObject = {
  authUserSession: AuthUserSession;
  sessionLoading: boolean;
  setSessionLoading: Dispatch<SetStateAction<boolean>>;
  logout: () => void;
};

export const useSession = (): UseSessionObject => {
  const [sessionLoading, setSessionLoading] = useState<boolean>(true);
  const [authUserSession, setAuthUserSession] = useState<AuthUserSession>(null);
  const { setOnboardingLoading } = useOnboardingStateContext();
  const canStopSessionLoadingRef = useRef(true);
  const appDispatch = useAppDispatch();

  const resetReduxStore = useCallback(() => {
    appDispatch(resetStoreAction());
  }, [appDispatch]);

  const queryClient = useQueryClient();

  const setUserProfileData = (userData: EverysetUser) => {
    appDispatch(setUserProfile(userData));
  };
  const setUserAppearanceData = (userAppearance: UserAppearance) => {
    appDispatch(setUserAppearance(userAppearance));
  };
  const router = useRouter();

  const captureAuthEvent = (event: 'tokenRefresh' | 'tokenRefresh_failure') => {
    if (!isPostHogLoaded()) {
      return;
    }

    posthog.capture(event);
  };

  const routerPathname = useMemo(() => {
    return removeTrailingSlashes(router?.pathname);
  }, [router?.pathname]);

  const isPublicApplicationView = useMemo(
    () => getIsPublicApplicationViewFromRouterAsPath(router?.asPath),
    [router?.asPath]
  );

  useEffect(() => {
    // initialize listener only once in constructor
    const result = Hub.listen('auth', (data: HubCapsule) =>
      AuthListener(
        data,
        (customState: string) =>
          setupSession(
            setAuthUserSession,
            setOnboardingLoading,
            setSessionLoading,
            canStopSessionLoadingRef,
            setUserProfileData,
            setUserAppearanceData,
            customState
          ),
        setSessionLoading,
        () => takedownSession(setAuthUserSession, appDispatch),
        captureAuthEvent
      )
    );

    // check for existing session on mount
    setupSession(
      setAuthUserSession,
      setOnboardingLoading,
      setSessionLoading,
      canStopSessionLoadingRef,
      setUserProfileData,
      setUserAppearanceData
    );

    // remove listener on takedown - just return listener result that has a remove callback
    return result;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(() => {
    setSessionLoading(true);

    Auth.signOut()
      .then((success) => {
        message.info('successfully signed out', success);

        setAuthUserSession(null);
        setSessionLoading(false);

        if (
          ROUTES_TO_RELOAD_WITHOUT_PUSHING_TO_INDEX.has(routerPathname) ||
          isPublicApplicationView
        ) {
          resetReduxStore();
          router.reload();
        } else {
          router.push('/').then(() => {
            queryClient.clear();
            resetReduxStore();
            router.reload();
          });
        }
      })
      .catch(() => {
        message.error('Error');
        setSessionLoading(false);
      });
  }, [
    setAuthUserSession,
    setSessionLoading,
    queryClient,
    resetReduxStore,
    router,
    routerPathname,
    isPublicApplicationView,
  ]);

  return { authUserSession, sessionLoading, setSessionLoading, logout };
};

const ROUTES_TO_RELOAD_WITHOUT_PUSHING_TO_INDEX = new Set([
  '/job-board',
  '/apps/[application_id]',
]);
